import { FC, useCallback, useEffect, useRef } from 'react';
import { PayPalNamespace, loadScript } from '@paypal/paypal-js';
import { OnApproveData } from '@paypal/paypal-js/types/components/buttons';
import { PayPalScriptOptions } from '@paypal/paypal-js/types/script-options';
import API_URLS from '../../../../../constants/api-urls';
import Routes from '../../../../../constants/routes';
import translation from '../../../../../languages';
import { useAppSelector } from '../../../../../store';
import axios from '../../../../../utils/axios';
import emailHash from '../../../../../utils/email-hash';
import isProduction from '../../../../../utils/is-production';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import { PAYMENT_METHOD_KEYS, PAYPAL_CLIENT_IDS } from '../../../payment.constants';
import { ErrorMessageType } from '../../error-message/error-message';
import { getLocale } from '../paypal-ppcp-controls/constants';
import qualityEvents from '../../../../../services/quality-events/quality-events';
import isMobileAppWithDeeplinkRedirect from '../../../../../utils/is-mobile-app';
import {
    setCheckoutSource
} from '../../../../../services/quality-events/quality-events-pages/checkout-page-quality-events';
import { getUserOS, getUserSID } from '../../../../../services/quality-events/quality-events.helpers';
import './paypal-express.scss';

type PaypalExpressProps = TranslationProps & {
    onError: (error: ErrorMessageType) => void;
};

const PaypalExpress: FC<PaypalExpressProps> = ({ onError, t }) => {
    const currency = useAppSelector((state) => state.main.currency);
    const user = useAppSelector((state: RootState) => state.main.user);
    const paymentCart = useAppSelector((state: RootState) => state.payment?.paymentCart);
    const paymentRef = useRef({ orderToken: '', transactionId: '' });

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await axios.get(API_URLS.PAYMENT_OPTIONS, {
                    params: {
                        currencyCode: currency.code
                    }
                });

                const supportedCurrency = response.currencies[PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT];
                const { userCountry } = response;
                const paypalIdToken = response.paypalCheckout?.id_token;
                const clientId = isProduction() ? PAYPAL_CLIENT_IDS.PRODUCTION : PAYPAL_CLIENT_IDS.STAGING;
                const ppScriptProps = {
                    clientId,
                    currency: supportedCurrency || 'EUR',
                    locale: getLocale(userCountry),
                    dataUserIdToken: paypalIdToken
                } as PayPalScriptOptions;

                if (userCountry) {
                    // For guest user
                    // PP express instead finish of the purchase will propose review the order and only after this will be purchase
                    if (!user.email) {
                        ppScriptProps.commit = false;
                    }

                    loadScript(ppScriptProps)
                        .then((paypalSDK) => {
                            renderPayPalButton(paypalSDK);
                        })
                        .catch((error) => {
                            console.info('failed to load the PayPal JS SDK script', error);
                        });
                }
            } catch (error: unknown) {
                const err = error as { data: { message: string } };

                console.log(err);
            }
        };

        if (currency.loaded) {
            fetchOptions();
        }
    }, [currency.loaded, user.email, currency.code]);

    const renderPayPalButton = (paypalSDK: PayPalNamespace | null) => {
        if (!paypalSDK) {
            return;
        }

        // @ts-ignore
        const button = paypalSDK.Buttons({
            createOrder,
            fundingSource: paypalSDK.FUNDING?.PAYPAL,
            onApprove,
            onCancel: () => {
                qualityEvents.checkoutPage.abandonedPayment(user.id, PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT, paymentCart?.finalPrice);
            },
            onError: (err) => {
                console.info('paypal btn error', err);
                qualityEvents.checkoutPage.failedPayment(
                    user.id,
                    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
                    paymentCart?.finalPrice,
                    (err as { message?: string })?.message
                );
            },
            style: {
                label: 'pay',
                height: 50,
                disableMaxWidth: true,
                // @ts-ignore
                borderRadius: 8,
            }
        });

        if (button.isEligible()) {
            button.render('#paypal-holder').catch((error) => {
                console.info('failed to render PayPal button', error);
            });
        } else {
            console.info('Paypal button is not eligible in this location');
        }
    };

    const createOrder = useCallback(() => {
        const params = serializeQueryParams();

        qualityEvents.cartPage.clickPPExpressButton(user?.id);

        if (!user.email) {
            return axios.post(`${API_URLS.PAYMENT_PP_EXPRESS_GUEST_TRANSACTION}`)
                .then((response) => {
                    paymentRef.current.transactionId = response.transactionId;

                    return response.transactionId;
                })
                .catch(catchAPIError);
        }

        emailHash.storeSHA256(user.email);

        return axios
            .post(`${API_URLS.PAYMENT_ATTEMPT}/PayPalCheckout`, {
                acceptAgb: 'on',
                currencyCode: currency.code,
                deepLinkRedirect: isMobileAppWithDeeplinkRedirect(),
                invoiceRequested: false,
                email: user.email,
                sid: getUserSID(),
                ...(getUserOS() && { os: getUserOS() }),
            })
            .then((response) => {
                paymentRef.current.orderToken = response.orderToken;

                return response.transactionId;
            })
            .catch(catchAPIError);
    }, [user.email]);

    const onApprove = useCallback((data: OnApproveData) => {
        const params = serializeQueryParams();

        if (!user.email) {
            setCheckoutSource('paypal');
            const ppQueryParams = `ppTransactionId=${paymentRef.current.transactionId}&defaultPaymentMethod=${PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT}`;
            const queryParams = window.location.search
                ? `${window.location.search}&${ppQueryParams}`
                : `?${ppQueryParams}`;

            qualityEvents.checkoutPage.initiateCheckout({
                cartTotalValue: paymentCart?.finalPrice ?? '',
                cartItems: paymentCart?.cartItems ?? [],
                internalPath: 'cart_page',
                onFinish: (): void => window.locationReload(`${Routes.PAYMENT.path}${queryParams}`)
            });

            return Promise.resolve();
        }

        return axios
            .post(`${API_URLS.PAYMENT_ATTEMPT_CALLBACK}/${paymentRef.current.orderToken}`, {
                deepLinkRedirect: isMobileAppWithDeeplinkRedirect(),
                transactionId: data.orderID
            })
            .then((response) => {
                setCheckoutSource('paypal');

                qualityEvents.checkoutPage.initiateCheckout({
                    cartTotalValue: paymentCart?.finalPrice ?? '',
                    cartItems: paymentCart?.cartItems ?? [],
                    internalPath: 'cart_page',
                    onFinish: (): void => {
                        window.location.href = response.redirect;
                    }
                });

                return response;
            })
            .catch(catchAPIError);
    }, [user.email]);

    const catchAPIError = (err?: { data?: { message?: string; reason?: string } }) => {
        qualityEvents.checkoutPage.failedPayment(
            user.id,
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
            paymentCart?.finalPrice,
            err?.data?.message || err?.data?.reason
        );
        onError({ message: t('cart.messages.paypalExpressError') });
    };

    return <div id='paypal-holder' />;
};

const translate = translation('cart');

export default translate(PaypalExpress);
